<template>
  <div v-if="!isBannerBuilder">
    <div class="d-flex align-items-center mt-2 mb-3 flex-wrap">
      <b-button
        v-for="(preset, key) in presets"
        :key="key"
        class="text-capitalize shadow-preset mb-2"
        :class="[`preset-${key}`]"
        :variant="`${key === activePreset ? '' : 'outline-'}primary`"
        @click="usePreset(key)"
      >
        {{ key }}
      </b-button>
      <b-button
        v-if="target && target.text"
        class="text-capitalize shadow-preset mb-2"
        :class="[`preset-outline`]"
        :variant="`${'outline' === activePreset ? '' : 'outline-'}primary`"
        @click="usePreset('outline')"
      >
        Outline
      </b-button>
    </div>
    <template v-if="selectedShadow === 'oval'">
      <OvalShadow
        :preset-data="presets['oval']"
        :target="target"
      />
    </template>
    <template v-else-if="selectedShadow === 'mirror'">
      <MirrorShadow
        :preset-data="presets['mirror']"
        :target="target"
      />
    </template>
    <template v-else-if="selectedShadow === 'outline'">
      <OutlineShadow :target="target" />
    </template>
    <template v-else-if="selectedShadow === 'projected'">
      <b-form-group label="Blur">
        <b-input-group>
          <b-input
            v-model="blur"
            :max="100"
            :min="0"
            type="range"
            @input="setShadow"
          />
          <template #append>
            <b-input
              v-model="blur"
              :max="100"
              :min="0"
              type="number"
              @input="setShadow"
            />
          </template>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Intensity">
        <b-input-group>
          <b-input
            v-model="intensity"
            :max="100"
            :min="0"
            type="range"
          />
          <template #append>
            <b-input
              v-model="intensity"
              :max="100"
              :min="0"
              type="number"
            />
          </template>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Color">
        <ColorPicker
          v-model="color"
          opacity
          copy
          position="right-middle"
          @input="setShadow"
        />
      </b-form-group>

      <ShadowProjection
        :target="target"
        @change="onProjectionChange"
      />
    </template>
    <template v-else>
      <b-form-group label="Blur">
        <b-input-group>
          <b-input
            v-model="blur"
            :max="100"
            :min="0"
            type="range"
            @input="setShadow"
          />
          <template #append>
            <b-input
              v-model="blur"
              :max="100"
              :min="0"
              type="number"
              @input="setShadow"
            />
          </template>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Distance">
        <b-input-group>
          <b-input
            v-model="distance"
            :max="200"
            :min="0"
            type="range"
            @input="calculateOffsets"
          />
          <template #append>
            <b-input
              v-model="distance"
              :max="200"
              :min="0"
              type="number"
              @input="calculateOffsets"
            />
          </template>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Angle">
        <b-input-group>
          <b-input
            v-model="angle"
            :max="360"
            :min="0"
            type="range"
            @input="calculateOffsets"
          />
          <template #append>
            <b-input
              v-model="angle"
              :max="360"
              :min="0"
              type="number"
              @input="calculateOffsets"
            />
          </template>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Intensity">
        <b-input-group>
          <b-input
            v-model="intensity"
            :max="100"
            :min="0"
            type="range"
          />
          <template #append>
            <b-input
              v-model="intensity"
              :max="100"
              :min="0"
              type="number"
            />
          </template>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Color">
        <ColorPicker
          v-model="color"
          opacity
          copy
          position="right-middle"
          @input="setShadow"
        />
      </b-form-group>
    </template>
  </div>
  <div v-else>
    <b-form-group label="Blur">
      <b-input-group>
        <b-input
          v-model="blur"
          :max="100"
          :min="0"
          type="range"
          @input="setShadow"
        />
        <template #append>
          <b-input
            v-model="blur"
            :max="100"
            :min="0"
            type="number"
            @input="setShadow"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <b-form-group
      label="Distance"
      :disabled="projection.enabled"
    >
      <b-input-group>
        <b-input
          v-model="distance"
          :max="200"
          :min="0"
          type="range"
          @input="calculateOffsets"
        />
        <template #append>
          <b-input
            v-model="distance"
            :max="200"
            :min="0"
            type="number"
            @input="calculateOffsets"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <b-form-group
      label="Angle"
      :disabled="projection.enabled"
    >
      <b-input-group>
        <b-input
          v-model="angle"
          :max="360"
          :min="0"
          type="range"
          @input="calculateOffsets"
        />
        <template #append>
          <b-input
            v-model="angle"
            :max="360"
            :min="0"
            type="number"
            @input="calculateOffsets"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <b-form-group label="Intensity">
      <b-input-group>
        <b-input
          v-model="intensity"
          :max="100"
          :min="0"
          type="range"
        />
        <template #append>
          <b-input
            v-model="intensity"
            :max="100"
            :min="0"
            type="number"
          />
        </template>
      </b-input-group>
    </b-form-group>
  </div>
</template>

<script>
import ColorPicker from './components/ColorPicker.vue'
import OvalShadow from "@frontend/components/modules/layouts-page/EditorControl/components/OvalShadow.vue";
import MirrorShadow from "@frontend/components/modules/layouts-page/EditorControl/components/MirrorShadow.vue";
import OutlineShadow from "@frontend/components/modules/layouts-page/EditorControl/components/OutlineShadow.vue";
import ShadowProjection from "@frontend/components/modules/layouts-page/EditorControl/components/ShadowProjection.vue";
import {DEFAULT_COLOR_STOPS, LEFT_LINEAR_GRADIENT_DIRECTION} from "@frontend/constants/oval-shadows";
import {clearOutlineShadow} from "@/common/preview/draw-multi-line-text";

export default {
  name: 'ShadowsModule',

  components: {
    ShadowProjection,
    OutlineShadow,
    OvalShadow,
    MirrorShadow,
    ColorPicker,
  },

  props: {
    target: {
      type: Object,
      default: () => ({}),
    },
    isBannerBuilder: {
      type: Boolean,
      default: false,
    },
    targets: {
      type: Array,
      default: () => ([]),
      required: false,
    },
  },

  data: () => ({
    angle: 0,
    distance: 0,
    color: null,
    blur: 0,
    offsetX: 0,
    offsetY: 0,
    projection: {
      enabled: false,
      value: [0,0,0,0,0,0,0,0],
      edit: false,
      fade: 0,
      _normalized: true,
    },
    presets: {
      none: {
        blur: 0,
        angle: 0,
        distance: 0,
        intensity: 50,
        color: '#00000080',
        projection: {
          enabled: false,
          value: [0,0,0,0,0,0,0,0],
          edit: false,
          _normalized: true,
        }
      },
      glow: {
        blur: 20,
        angle: 0,
        distance: 0,
      },
      drop: {
        blur: 15,
        angle: 145,
        distance: 28,
      },
      oval: {
        width: 80,
        height: 15,
        opacity: 50,
        offsetX: 10,
        offsetY: 90,
        blur: 30,
        color: '#000000',
      },
      mirror: {
        fade: 50,
        opacity: 50,
        offsetY: 0
      },
      projected: {
        blur: 20,
        angle: 0,
        distance: 0,
        color: '#00000080',
      },
    },
    selectedShadow: 'none'
  }),

  computed: {
    activePreset () {
      if (this.selectedShadow === 'oval') {
        return 'oval'
      }
      if (this.selectedShadow === 'mirror') {
        return 'mirror'
      }
      if (this.selectedShadow === 'outline') {
        return 'outline'
      }
      return this.selectedShadow;
    },
    intensity: {
      get () {
        // Convert hex alpha (00-FF) to decimal and scale it to 0-100
        const alphaHex = this.color?.length === 9 ? this.color.slice(-2) : 'FF'
        return Math.round(parseInt(alphaHex, 16) / 255 * 100)
      },
      set (val) {
        // Convert intensity (0-100) to hex alpha (00-FF)
        let alphaHex = Math.round(val / 100 * 255).toString(16).padStart(2, '0')
        let baseColor = this.color.length === 9 ? this.color.substring(0, 7) : this.color
        baseColor += alphaHex
        this.color = baseColor
        this.setShadow()
      },
    },
  },

  watch: {
    target: {
      handler (val) {
        this.selectedShadow = 'none';
        if (val?.outlineShadow?.outline) {
          this.selectedShadow = 'outline';
          return;
        }
        if (Array.isArray(val?.ovalShadow) && val.ovalShadow.length > 0) {
          this.selectedShadow = 'oval';
          return;
        }
        if (Array.isArray(val?.mirrorShadow) && val.mirrorShadow.length > 0) {
          this.selectedShadow = 'mirror';
          return;
        }
        const { angle, distance, color, blur, offsetX, offsetY } = val?.shadow || this.presets.none
        this.angle = angle
        this.distance = distance
        this.color = color
        this.blur = blur
        this.offsetX = Math.round(offsetX)
        this.offsetY = Math.round(offsetY)
        if (val?.shadow?.type && val.shadow.type !== undefined) {
          this.selectedShadow = val.shadow.type;
        } else if (this.isBannerBuilder && val?.shadow) {
          this.selectedShadow = 'drop';
        }
        const proj = val?.shadow?.projection;
        this.projection = {
          enabled: !!proj?.enabled,
          edit: proj?.edit || false,
          value: Array.isArray(proj?.value) && proj.value.length === 8
            ? [...proj.value]
            : [0, 0, 0, 0, 0, 0, 0, 0],
          fade: proj?.fade || 0,
          _normalized: proj?._normalized ?? true,
        };
        if (this.projection.enabled) {
          this.selectedShadow = 'projected';
        }
        // Set up listener for shadow dragging
        this.setupShadowDragListener(val)
      },
      immediate: true,
    },
  },

  mounted() {
    // Clean up event listener when component is destroyed
    this.$once('hook:beforeDestroy', () => {
      if (this.target?.canvas) {
        this.target.canvas.off('shadow:dragged', this.handleShadowDragged)
      }
    })
  },

  methods: {
    setEnabled(enabled) {
      if (!enabled) {
        this.selectedShadow = 'none';
        this.setShadow();
        return;
      }

      if (this.selectedShadow === 'none') {
        this.selectedShadow = 'drop';
        const { blur, angle, distance } = this.presets.drop;
        this.blur = blur;
        this.angle = angle;
        this.distance = distance;
        this.color = this.color || this.presets.none.color;
      }
      this.calculateOffsets();
    },
    setupShadowDragListener(target) {
      // Remove old listener if exists
      if (this._lastTarget?.canvas) {
        this._lastTarget.canvas.off('shadow:dragged', this.handleShadowDragged)
      }

      // Add new listener if target has canvas
      if (target?.canvas) {
        target.canvas.on('shadow:dragged', this.handleShadowDragged)
        this._lastTarget = target
      }
    },

    handleShadowDragged(event) {
      // Only update if the dragged target is the currently selected target
      if (event.target !== this.target) {
        return
      }

      const shadow = event.shadow
      if (!shadow) {
        return
      }

      // Update component state from dragged shadow values
      this.angle = shadow.angle || 0
      this.distance = shadow.distance || 0
      this.offsetX = Math.round(shadow.offsetX || 0)
      this.offsetY = Math.round(shadow.offsetY || 0)
      // Note: blur, color, and intensity are not changed during dragging
    },

    setShadow () {
      if (this.targets.length > 0) {
        this.targets.forEach(target => {
          if (this.selectedShadow === 'none') {
            target.set('shadow', null);
            target.canvas.renderAll()
            target.canvas.fire('shadows:changed', target);
            return;
          }

          target.set('shadow', {
            type: this.selectedShadow,
            angle: +this.angle,
            distance: +this.distance,
            color: this.color,
            blur: +this.blur,
            offsetX: this.offsetX,
            offsetY: this.offsetY,
            nonScaling: true,
            projection: { ...this.projection }
          });
          target.canvas.renderAll()
          target.canvas.fire('shadows:changed', target);
        });
        return;
      }

      if (!this.target) {
        return
      }

      if (this.selectedShadow === 'none') {
        this.target.set('shadow', null)
        this.target.canvas.renderAll()
        this.target.canvas.fire('shadow:changed', this.target)
        return
      }

      this.target.set('shadow', {
        type: this.selectedShadow,
        angle: +this.angle,
        distance: +this.distance,
        color: this.color,
        blur: +this.blur,
        offsetX: this.offsetX,
        offsetY: this.offsetY,
        nonScaling: true,
        projection: this.projection
      })
      this.target.canvas.renderAll()
      this.target.canvas.fire('shadow:changed', this.target)
    },
    usePreset (key) {
      this.selectedShadow = key;

      if (!this.target) {
        return;
      }

      // Projection only applies to the dedicated "projected" type;
      // clear it when switching to any other shadow type.
      if (key !== 'projected') {
        this.projection = {
          enabled: false,
          edit: false,
          value: this.projection.value?.length === 8
            ? this.projection.value
            : [0, 0, 0, 0, 0, 0, 0, 0],
          fade: this.projection.fade || 0,
          _normalized: true,
        };
      }

      if (key !== 'outline') {
        clearOutlineShadow(this.target);
      }

      if (key === 'projected') {
        const preset = this.presets.projected;
        this.target.set('mirrorShadow', null);
        this.target.set('ovalShadow', null);
        this.target.set('ovalShadowGroupId', null);
        this.angle = preset.angle;
        this.distance = preset.distance;
        this.offsetX = 0;
        this.offsetY = 0;
        this.blur = preset.blur;
        this.color = preset.color;
        this.projection = {
          enabled: true,
          edit: false,
          value: this.projection.value?.length === 8
            ? this.projection.value
            : [0, 0, 0, 0, 0, 0, 0, 0],
          fade: this.projection.fade || 0,
          _normalized: true,
        };
        this.setShadow();
        return;
      }

      if (key === 'outline') {
        this.target.set('mirrorShadow', null);
        this.target.set('ovalShadow', null);
        this.target.set('ovalShadowGroupId', null);
        this.target.set('shadow', null);
        if (typeof this.target.updateMirrorShadowData === 'function') {
          this.target.updateMirrorShadowData();
        }
        if (typeof this.target.updateOvalShadowData === 'function') {
          this.target.updateOvalShadowData();
        }
        return
      }
      
      const { blur, angle, distance, color } = this.presets[key];
      
      this.target.set('mirrorShadow', null);
      this.target.set('ovalShadow', null);
      this.target.set('ovalShadowGroupId', null);

      if (key === 'oval') {
        this.target.set('shadow', null);

        if (!Array.isArray(this.target.ovalShadow) || this.target.ovalShadow.length === 0) {
          const preset = this.presets.oval;

          const defaultOvalShadow = {
            width: preset.width / 100,
            height: preset.height / 100,
            blur: preset.blur,
            offsetX: preset.offsetX / 100,
            offsetY: preset.offsetY / 100,
            opacity: preset.opacity / 100,
            displayMode: 'behind',
            color: preset.color,
            angle: 0,
            fillType: 'color',
            linearDirection: 'left',
            colorStops: [...DEFAULT_COLOR_STOPS],
            disabled: false,
          };

          this.target.set('ovalShadow', [defaultOvalShadow]);
        }

        if (typeof this.target.updateOvalShadowData === 'function') {
          this.target.updateOvalShadowData();
        }

        this.target.canvas?.renderAll();
        this.target.canvas?.fire('shadow:changed', this.target);
        return;
      }

      if (key === 'mirror') {
        this.target.set('shadow', null);

        if (!Array.isArray(this.target.mirrorShadow) || this.target.mirrorShadow.length === 0) {
          const preset = this.presets.mirror;

          const defaultMirrorShadow = {
            fade: preset.fade,
            opacity: preset.opacity / 100,
            offsetY: preset.offsetY,
            linearDirection: LEFT_LINEAR_GRADIENT_DIRECTION,
            disabled: false,
          };

          this.target.set('mirrorShadow', [defaultMirrorShadow]);
        }

        if (typeof this.target.updateMirrorShadowData === 'function') {
          this.target.updateMirrorShadowData();
        }

        this.target.canvas?.renderAll();
        this.target.canvas?.fire('shadow:changed', this.target);
        return;
      }

      if (typeof this.target.updateMirrorShadowData === 'function') {
        this.target.updateMirrorShadowData();
      }
      if (typeof this.target.updateOvalShadowData === 'function') {
        this.target.updateOvalShadowData();
      }
      this.angle = angle
      this.distance = distance
      this.blur = blur
      if (color) {
        this.color = color
      }
      this.calculateOffsets()
    },
    calculateOffsets () {
      const angleRadians = this.angle * (Math.PI / 180)
      this.offsetX = Math.round(Math.cos(angleRadians) * -this.distance)
      this.offsetY = Math.round(Math.sin(angleRadians) * this.distance)
      this.setShadow()
    },
    onProjectionChange(projection) {
      this.projection = projection;
      this.setShadow();
    },
  },
}
</script>

<style scoped lang="scss">
.preset {
  &-none {
    box-shadow: none;
  }

  &-glow {
    box-shadow: 0 0 8px rgb(0, 0, 0);
  }

  &-mirror {
    box-shadow: none;
  }

  &-drop {
    box-shadow: 5px 5px 10px rgb(0, 0, 0);
  }
  
  &-outline {
    box-shadow: 0 0 0 2px rgb(0, 0, 0);
  }

  &-projected {
    box-shadow: 8px 8px 6px -4px rgb(0, 0, 0);
  }
}
.shadow-preset {
  flex: 0 0 30%;
  margin-right: 10px;
  &:nth-child(3n) {
    margin-right: 0;
  }
}
</style>
